import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectFilteredInventories,
  selectInventoryFilterVisible,
  selectInventoryLoading,
  selectfetchInventoryLoading,
} from './store/selectors';
import VirtualList from '../components/VirtualList'; // Import VirtualList
import LoadingSpinner from '../../InitialPage/Sidebar/LoadingSpinner'; // Import LoadingSpinner
import { isHeaderVisible } from 'Core/redux/main/selectors';
import { MODAL_TYPES } from 'Feature/components/app-modals';
import { showModal } from 'Core/redux/main/slice';
import { Edit, Trash2 } from 'react-feather';
import { Button, ButtonGroup } from 'react-bootstrap';
import { deleteInventory, fetchInventorySerialData } from './store/slice';

const { INVENTORY_ADD_EDIT } = MODAL_TYPES;

const InventoryTile = ({ selectedType, onOpenDetailModal }) => {
  const [columnCount, setColumnCount] = useState(6);
  const [columnWidth, setColumnWidth] = useState(300);
  const inventoryTileRef = useRef(null);
  const filteredInventories = useSelector(selectFilteredInventories);
  const isLoading = useSelector(selectInventoryLoading);
  const filterVisible = useSelector(selectInventoryFilterVisible);
  const headerVisible = useSelector(isHeaderVisible);
  const isFetchDataLoading = useSelector(selectfetchInventoryLoading);

  const filteredByType = filteredInventories.filter(item => {
    if (['FG', 'PL', 'IN'].includes(item.class)) {
      return item.class === selectedType;
    }

    return selectedType === 'Others';
  });

  const updateColumnSettings = useCallback(() => {
    if (inventoryTileRef.current) {
      const width = inventoryTileRef.current.offsetWidth;
      let columns = 6;

      if (width < 576) {
        columns = 1; // Mobile
      } else if (width < 768) {
        columns = 3; // Tablets
      } else if (width < 992) {
        columns = 5; // Large screens
      } else {
        columns = 6; // Extra large screens
      }

      setColumnCount(columns);
      setColumnWidth(width / columns);
    }
  }, []);

  useEffect(() => {
    updateColumnSettings();
    window.addEventListener('resize', updateColumnSettings);
    return () => window.removeEventListener('resize', updateColumnSettings);
  }, [updateColumnSettings]);

  const itemData = filteredByType.map(inventory => <Tile key={inventory.processId || inventory.id || Math.random()} inventory={inventory} onOpenDetailModal={onOpenDetailModal} />);

  return (
    <div
      className={`shipment-tile-body ${filterVisible ? 'filter-visible' : ''} ${
        !headerVisible ? 'header-collapsed' : ''
      }`}
      ref={inventoryTileRef}
    >
      {(isLoading || isFetchDataLoading) && <LoadingSpinner />}
      
      {
        <VirtualList
          columnCount={columnCount}
          rowCount={Math.ceil(filteredByType.length / columnCount)}
          itemData={itemData}
          columnWidth={columnWidth}
          rowHeight={150} // Adjusted row height
        />
      }
    </div>
  );
};

const Tile = ({ inventory, onOpenDetailModal }) => {
  const dispatch = useDispatch();

  const handleEdit = e => {
    e.stopPropagation();
    if (onOpenDetailModal) {
      // Pass inventory type information to help the modal determine which API to call
      const modalData = {
        ...inventory,
        inventoryType: inventory.inventoryType || 'Production' // Default to Production if not specified
      };

      onOpenDetailModal(modalData);
    }
  };

  const handleDelete = e => {
    e.stopPropagation();
    //dispatch(deleteInventory(inventory.processId));
  };

  // Function to get tile styling based on inventory type
  const getTileStyle = () => {
    if (inventory.inventoryType === 'Production') {
      return {
        border: '1px solid #28a745',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        backgroundColor: '#ffffff',
        borderLeft: '4px solid #28a745'
      };
    } else if (inventory.inventoryType === 'In House') {
      return {
        border: '1px solid #007bff',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        backgroundColor: '#ffffff',
        borderLeft: '4px solid #007bff'
      };
    } else {
      return {
        border: '1px solid #ddd',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        backgroundColor: '#ffffff'
      };
    }
  };

  // Function to get type badge styling
  const getTypeBadgeStyle = () => {
    if (inventory.inventoryType === 'Production') {
      return {
        backgroundColor: '#e8f5e8',
        color: '#28a745',
        fontSize: '0.65rem',
        fontWeight: '500',
        border: '1px solid #28a745'
      };
    } else if (inventory.inventoryType === 'In House') {
      return {
        backgroundColor: '#e8f4ff',
        color: '#007bff',
        fontSize: '0.65rem',
        fontWeight: '500',
        border: '1px solid #007bff'
      };
    } else {
      return {
        backgroundColor: '#f8f9fa',
        color: '#6c757d',
        fontSize: '0.65rem',
        fontWeight: '500',
        border: '1px solid #6c757d'
      };
    }
  };

  return (
    <div key={inventory.processId || inventory.id || Math.random()} className="shipment-tile inventory-tile">
      <div
        className="card p-3 m-2"
        style={getTileStyle()}
      >
        <div className="tile-actions">
          <ButtonGroup size="sm">
            <Button variant="light" onClick={handleEdit}>
              <Edit size={16} />
            </Button>
            {/* <Button variant="light" onClick={handleDelete}>
              <Trash2 size={16} />
            </Button> */}
          </ButtonGroup>
          
          {/* Inventory Type Badge - Top Right */}
          {inventory.inventoryType && (
            <span
              className="badge ms-2"
              style={getTypeBadgeStyle()}
            >
              {inventory.inventoryType}
            </span>
          )}
        </div>
        
        <div className="d-flex justify-content-between">
          <div className="d-flex flex-row align-items-center">
            <div className="ms-2 c-details">
              <h6 className="mb-0">{inventory.partNumber} </h6>
              <span className="text-muted fa-sm">{inventory.fromDate}</span> -{' '}
              <span className="text-muted fa-sm">{inventory.toDate}</span>
            </div>
          </div>
        </div>



        {/* Status Badge */}
        <div className="mt-1">
          <span
            className="badge"
            style={{
              backgroundColor: inventory.backgroundColor || '#e0e0e0',
              color: inventory.textColor || '#000000',
            }}
          >
            {inventory.status}
          </span>
        </div>

        {/* Location/Machine */}
        <div className="mt-1">
          <h5 className="heading set-ellipsis" title={inventory.locationName}>
            {inventory.locationName}
          </h5>
        </div>

        {/* Additional Production Information */}
        <div className="mt-1">
          {inventory.machineName && (
            <span className="badge bg-secondary me-1" style={{ fontSize: '0.6rem' }}>
              Machine: {inventory.machineName}
            </span>
          )}
          {inventory.partClass && (
            <span className="badge bg-dark me-1" style={{ fontSize: '0.6rem' }}>
              Class: {inventory.partClass}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default InventoryTile;

